import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { fabric } from 'fabric';

import { Canvas } from '~/components/Canvas';
import { ImageContainer } from '~/components/ImageContainer';
import { ImageContextMenu } from '~/components/ImageContextMenu';
import { useCanvas } from '~/contexts/CanvasContext';
import { useNotification } from '~/contexts/NotificationContext';
import { ICamera } from '~/domain/ICamera';
import { IImageSettings } from '~/domain/IImageSettings';
import { ResponseError } from '~/domain/ResponseError';
import { useContextMenu } from '~/hooks/useContextMenu';
import {
  useCursor,
  useDeleteSelection,
  useDrawAnnotation,
  useEraser,
  useImage,
  useRectangleAnnotation,
  useSelection,
  useTextAnnotation,
  useZoom,
} from '~/hooks/useFabric';
import { useImagePrefetch, useImageQuery } from '~/hooks/useImage';
import {
  ArrowLeftKey,
  ArrowRightKey,
  EndKey,
  HomeKey,
  isAnyModalOpened,
  isInputFocused,
} from '~/keyboard';
import { NOOP } from '~/NOOP';
import * as LAYOUT from '~/pages/layout';
import { useAppDispatch } from '~/redux';
import { addImageError } from '~/redux/features/imageErrorsSlice';

import { AnnotateContainer } from './AnnotateContainer';
import { AnnotationToolbar } from './AnnotationToolbar';

interface IUser360Viewer {
  containerWidth: number;
  camera: ICamera;
  settings: IImageSettings;
  settings4K: IImageSettings;
  //imageCount: number;
}

const ROTATE_THRESHOLD = 10;
const CANVAS_OPTIONS = {
  defaultCursor: 'grab',
};

let imageCount: number;

export const Use360Viewer: React.FC<IUser360Viewer> = (props) => {
  const dispatch = useAppDispatch();
  const { camera, containerWidth, settings, settings4K } = props;  
  imageCount = camera.frames;
  const { aspectRatio } = settings;
  const ref = useRef() as React.MutableRefObject<fabric.Canvas>;
  const [searchParams, setSearchParams] = useSearchParams();
  const waypointParam = parseInt(searchParams.get('waypoint') || '0', 10);
  const [waypoint, setWaypoint] = useState(waypointParam);
  const prevPointerRef = useRef({ x: 0, y: 0 });
  const isMouseDownRef = useRef(false);
  const notify = useNotification();
  const context = useCanvas();
  const {
    isFetching,
    isError,
    isInitialLoading,
    data: src,
    refetch,
    error,
  } = useImageQuery({ camera, settings: { ...settings, cameraWaypoint: waypoint }, signal: false });
  const [anchorPosition, contextMenu] = useContextMenu({ isFetching, isError });
  const { annotationEnabled } = context;

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const canvas = ref.current;
    if (canvas) {
      isMouseDownRef.current = true;
      prevPointerRef.current = canvas.getPointer(event as unknown as Event);
    }
  };

  const prefetchConfig = useMemo(() => {
    return Array.from(Array(imageCount).keys())
      .map(cameraWaypoint => {
        return [camera, { ...settings, cameraWaypoint }] as [ICamera, IImageSettings];
      });
  }, [settings, camera]);

  const prefetch = useImagePrefetch(prefetchConfig);

  // Initialization mouse up/down
  // abort prefetching
  useEffect(() => {
    const onPointerUp = (event: PointerEvent) => {
      if (event.isPrimary) {
        isMouseDownRef.current = false;
      }
    };
    if (!annotationEnabled) {
      document.addEventListener('pointerup', onPointerUp);
      return () => {
        document.removeEventListener('pointerup', onPointerUp);
      };
    }
  }, [annotationEnabled]);

  // Tracks the mouse movements and determines the image to use
  useEffect(() => {
    const canvas = ref.current;
    const onPointerMove = (event: PointerEvent) => {
      if (isMouseDownRef.current && canvas.getZoom() === 1) {
        const currPointer = canvas.getPointer(event);
        const diff = currPointer.x - prevPointerRef.current.x;

        if (Math.abs(diff) < ROTATE_THRESHOLD) {
          return;
        }

        let value = diff > 0 ? waypoint + 1 : waypoint - 1;
        value = (value + imageCount) % imageCount;
        setWaypoint(value);
        prevPointerRef.current = currPointer;
      }
    };
    if (!annotationEnabled) {
      document.addEventListener('pointermove', onPointerMove);
      return () => {
        document.removeEventListener('pointermove', onPointerMove);
      };
    }
  }, [annotationEnabled, waypoint, setSearchParams]);

  useEffect(() => {
    const handleNavigation = (e: KeyboardEvent) => {
      if (!isAnyModalOpened() && !isInputFocused()) {
        if ([HomeKey, EndKey].includes(e.key)) {
          setWaypoint(e.key === HomeKey ? 0 : imageCount / 2);
        } else if ([ArrowRightKey, ArrowLeftKey].includes(e.key)) {
          let value = e.key === ArrowRightKey ? waypoint + 1 : waypoint - 1;
          value = (value + imageCount) % imageCount;
          setWaypoint(value);
        }
      }
    };
    if (!annotationEnabled) {
      document.addEventListener('keydown', handleNavigation);
      return () => {
        document.removeEventListener('keydown', handleNavigation);
      }
    }
  }, [annotationEnabled, setSearchParams, waypoint]);

  useEffect(() => {
    setWaypoint(waypointParam);
  }, [waypointParam]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearchParams((prev) => {
        return {
          ...Object.fromEntries(prev),
          waypoint: waypoint ? `${waypoint}` : [],
        }
      });
    }, 250);
    return () => {
      window.clearTimeout(timeout);
    }
  }, [waypoint, setSearchParams]);

  useImage({
    ref,
    src,
    aspectRatio,
  });

  useSelection({
    ref,
    context,
  });

  useCursor({
    ref,
    context,
    defaultCursor: 'grab',
  });

  useRectangleAnnotation({
    ref,
    context,
  });

  useDrawAnnotation({
    ref,
    context,
  });

  useTextAnnotation({
    ref,
    context,
  });

  useEraser({
    ref,
    context,
  });

  useDeleteSelection({
    ref,
    context,
  });

  useZoom({
    ref,
    context,
  });

  useEffect(() => {
    if (isError) {
      if (error) {
        const respError = error as ResponseError;
        dispatch(addImageError(respError.message));
      }
      notify({ type: 'warning', message: 'We couldn’t load this image. Please refresh or try again later' });
    }
  }, [isError, error, notify, dispatch]);

  return (
    <>
      <AnnotateContainer
        isAnnotating={annotationEnabled}
        containerWidth={containerWidth}
        onContextMenu={contextMenu.onContextMenu}
      >
        <ImageContainer
          aspectRatio={aspectRatio}
          isFetching={isFetching}
          isError={isError}
          isInitialLoading={isInitialLoading}
          refetch={() => {
            refetch();
            prefetch();
          }}
          onPointerDown={!annotationEnabled ? handlePointerDown : NOOP}
        >
          <Canvas ref={ref} defaultOptions={CANVAS_OPTIONS} sx={LAYOUT.IMAGE} />
        </ImageContainer>
        <AnnotationToolbar canvasRef={ref} disabled={isError || isFetching} />
      </AnnotateContainer>
      <ImageContextMenu
        anchorPosition={anchorPosition}
        context={contextMenu}
        camera={camera}
        images={[
          { label: 'HD', settings: { ...settings, cameraWaypoint: waypoint } },
          { label: '4K', settings: { ...settings4K, cameraWaypoint: waypoint } },
        ]}
      />
    </>
  );
};
